import { createRoom, fetchReplayBlob, listHighScores, listRooms, prepareJoin } from './net/api.js'
import { mountReplayer } from './replayer/Replayer.js'

const NAME_KEY = 'asteroids.name'

export function createInitialMenuState() {
  return structuredClone({
    name: localStorage.getItem(NAME_KEY) || '',
    roomName: '',
    createPrivate: false,
    selectedPrivateRoomId: null,
    joinCode: '',
    scoreModeFilter: 'all'
  })
}

export function formatServerError(message) {
  switch (message) {
    case 'no_such_room': return 'That game is no longer available.'
    case 'room_full': return 'That game is already full.'
    case 'game_in_progress': return 'That game is already in progress.'
    case 'bad_room_code': return 'That private game code is incorrect.'
    case 'already_in_room': return 'You are already in a game.'
    case 'bad_json': return 'The server rejected a malformed message.'
    default: return message || 'Server error'
  }
}

export async function refreshData(app) {
  try {
    const [roomsData, scoresData] = await Promise.all([
      listRooms(),
      listHighScores(app.menu.scoreModeFilter)
    ])

    const nextRooms = roomsData.rooms ?? []
    const nextScores = scoresData.highScores ?? []
    const wasConnected = app.connected

    const prevSel = app.menu.selectedPrivateRoomId
    const prevJoin = app.menu.joinCode

    if (app.view === 'menu') {
      const selectedExists = nextRooms.some(room => room.id === app.menu.selectedPrivateRoomId)
      if (!selectedExists) {
        app.menu.selectedPrivateRoomId = null
        app.menu.joinCode = ''
      }
    }

    const menuMutated =
      prevSel !== app.menu.selectedPrivateRoomId || prevJoin !== app.menu.joinCode

    const sameRooms = JSON.stringify(app.rooms) === JSON.stringify(nextRooms)
    const sameScores = JSON.stringify(app.highScores) === JSON.stringify(nextScores)

    app.connected = true
    app.rooms = nextRooms
    app.highScores = nextScores

    if (app.view === 'menu') {
      const dataChanged = menuMutated || !sameRooms || !sameScores || !wasConnected
      if (dataChanged) app.render()
    }
  } catch {
    const wasConnected = app.connected
    app.connected = false
    app.rooms = []
    app.highScores = []
    if (app.view === 'menu' && wasConnected) app.render()
  }
}

export function setName(app, value) {
  app.menu.name = value
  localStorage.setItem(NAME_KEY, value)
}

export function setRoomName(app, value) {
  app.menu.roomName = value
}

export function setCreatePrivate(app, value) {
  app.menu.createPrivate = value
  app.render()
}

export function setJoinCode(app, value) {
  app.menu.joinCode = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4)
}

export function togglePrivateRoom(app, roomId) {
  const prev = app.menu.selectedPrivateRoomId
  const next = prev === roomId ? null : roomId
  app.menu.selectedPrivateRoomId = next
  app.menu.joinCode = ''
  if (prev !== next) {
    app.error = null
    app.errorPlacement = null
  }
  app.render()
}

export function setScoreModeFilter(app, value) {
  app.menu.scoreModeFilter = ['all', 'coop', 'deathmatch', 'ctf'].includes(value) ? value : 'all'
  if (app.view === 'menu') app.render()
  void refreshData(app)
}

export function clearMenuError (app) {
  if (app.error == null) return
  app.error = null
  app.errorPlacement = null
  app.render()
}

export async function createGame(app) {
  try {
    app.error = null
    app.errorPlacement = null
    app.audio.resume()
    const roomName = app.menu.roomName.trim() || `${app.menu.name.trim()}’s game`
    const room = await createRoom(roomName, app.menu.createPrivate)
    await app.connectToRoom({
      name: app.menu.name.trim(),
      wsPath: room.wsPath,
      roomCode: room.roomCode ?? ''
    })
  } catch (error) {
    app.error = formatServerError(error.message || 'Could not create room')
    app.errorPlacement = 'global'
    app.render()
  }
}

export async function joinRoom(app, roomId, roomCode) {
  try {
    app.error = null
    app.errorPlacement = null
    app.audio.resume()
    const room = await prepareJoin(roomId, roomCode)
    await app.connectToRoom({
      name: app.menu.name.trim(),
      wsPath: room.wsPath,
      roomCode
    })
  } catch (error) {
    app.error = formatServerError(error.message || 'Could not join room')
    app.errorPlacement = error.message === 'bad_room_code' ? 'join-code' : 'global'
    app.render()
    await refreshData(app)
  }
}

export function leaveRoom(app) {
  app.error = null
  app.errorPlacement = null
  app.disconnectMessage = null
  app.roomSocket.leaveRoom()
  app.roomSocket.close(true)
  app.lobby = null
  app.myId = null
  app.gameOver = null
  app.roundStart = null
  app.view = 'menu'
  app.render()
  void refreshData(app)
}

export async function openReplay(app, replayId) {
  if (document.querySelector('.replay-viewer:popover-open')) return
  try {
    const buf = await fetchReplayBlob(replayId)
    const recap = document.getElementById('lobby-last-match')
    const anchorOpts = recap && recap.matches(':popover-open') ? { anchor: recap } : {}
    mountReplayer(buf, { ...anchorOpts, replayId })
  } catch {
    app.error = 'Replay failed.'
    app.errorPlacement = 'global'
    app.render()
  }
}

export function consumeReplayQueryOnLoad(app) {
  const raw = new URLSearchParams(window.location.search).get('replay')
  const id = raw != null ? String(raw).trim() : ''
  if (!id) return
  if (document.querySelector('.replay-viewer:popover-open')) return
  void openReplay(app, id)
}
